import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type { SysmlApiAdapter } from '../api/api-adapter.js';
import type {
  ChatMessage,
  ChatPhase,
  ChatResponse,
  GenerateResponse,
  ModelContext,
  QualityFeedback,
  QualityScore,
  SysmlChatbotModel,
  SysmlChatbotModelSummary,
  SysmlModelBehavior,
  SysmlModelPart,
  SysmlModelRequirement,
  SysmlModelRisk,
  SysmlModelState,
  SysmlModelVerificationCase,
  SysmlSuggestedOutput,
  TraceabilityMatrix,
  UpdateModelStateResult,
  VersionHistoryEntry,
} from '../api/types.js';
import { STORAGE_KEYS } from '../utils/constants.js';

// ---------------------------------------------------------------------------
// ChatbotController — AI chatbot state with full model management
//
// Ported from: sysmlChatbotStore.ts (Pinia/Vue)
// Pattern: Lit ReactiveController
//
// Features:
//   - Model CRUD (create, load, list, delete)
//   - Model state tracking (parts, behaviors, requirements, risks, verifications)
//   - Phase progression: exploring -> ready -> generating
//   - Completeness scoring: parts*10 + behaviors*20 + requirements*20 + risks*10
//   - INCOSE requirement quality assessment
//   - Traceability: trace coverage % (requirements traced to parts)
//   - Verification coverage: % of requirements with verification cases
//   - Suggested SysML source text with file exports
//   - Direct state updates (addRequirement, addPart, addBehavior, etc.)
//   - Link verification to requirement
//   - Version history with revert
// ---------------------------------------------------------------------------

const MODEL_STORAGE_KEY = 'sysml-chatbot-model-id';

export class ChatbotController implements ReactiveController {
  private host: ReactiveControllerHost & HTMLElement;
  private api!: SysmlApiAdapter;
  private _syncTimeout: ReturnType<typeof setTimeout> | null = null;

  // ── Core state ──────────────────────────────────────────────────────────

  generation = 0;

  /** Current model ID */
  modelId: string | null = null;

  /** Current model data (full state) */
  model: SysmlChatbotModel | null = null;

  /** List of available models */
  models: SysmlChatbotModelSummary[] = [];

  /** Conversation messages */
  messages: ChatMessage[] = [];

  /** Current phase (exploring, ready, generating) */
  phase: ChatPhase = 'exploring';

  /** Current model state (parts, behaviors, requirements, risks, verifications) */
  modelState: SysmlModelState | null = null;

  /** Completeness score (0-100) */
  completenessScore: number | null = null;

  /** Suggested next prompts */
  suggestions: string[] = ['A coffee machine', 'A smart thermostat', 'A drone system'];

  /** Validation issues from the model */
  validationIssues: string[] = [];

  /** Last suggested source text from chat */
  suggestedSourceText = '';

  /** Last suggested output files from generation */
  suggestedOutputs: SysmlSuggestedOutput[] = [];

  /** Context summary for conversation continuity */
  contextSummary: string | null = null;

  /** INCOSE quality feedback for requirements */
  qualityFeedback: QualityFeedback | null = null;

  /** Traceability matrix for requirements */
  traceabilityMatrix: TraceabilityMatrix | null = null;

  /** Legacy quality score (for backward compat with panel) */
  qualityScore: QualityScore | null = null;

  // ── Loading flags ───────────────────────────────────────────────────────

  isLoading = false;
  isGenerating = false;
  isLoadingModels = false;
  error: string | null = null;

  // ── Computed-like getters ───────────────────────────────────────────────

  get hasActiveModel(): boolean {
    return this.modelId !== null && this.model !== null;
  }

  get partCount(): number {
    return this.modelState?.parts?.length ?? 0;
  }

  get behaviorCount(): number {
    return this.modelState?.behaviors?.length ?? 0;
  }

  get requirementCount(): number {
    return this.modelState?.requirements?.length ?? 0;
  }

  get riskCount(): number {
    return this.modelState?.risks?.length ?? 0;
  }

  get verificationCount(): number {
    return this.modelState?.verificationCases?.length ?? 0;
  }

  get verificationCases(): SysmlModelVerificationCase[] {
    return this.modelState?.verificationCases ?? [];
  }

  get isReadyToGenerate(): boolean {
    return this.phase === 'ready' || (this.completenessScore ?? 0) >= 70;
  }

  get phaseLabel(): string {
    switch (this.phase) {
      case 'exploring':
        return 'Exploring requirements...';
      case 'ready':
        return 'Ready to generate';
      case 'generating':
        return 'Generating SysML...';
      default:
        return 'Unknown';
    }
  }

  /** Overall INCOSE quality score (0-100) */
  get qualityOverallScore(): number | null {
    return this.qualityFeedback?.overallScore ?? null;
  }

  /** Whether any requirements have quality issues */
  get hasQualityIssues(): boolean {
    if (!this.qualityFeedback?.requirementScores) return false;
    return Object.values(this.qualityFeedback.requirementScores).some(
      (r) => r.issues.length > 0,
    );
  }

  /** Trace coverage percentage (0-100) */
  get traceCoverage(): number | null {
    if (
      !this.traceabilityMatrix ||
      this.traceabilityMatrix.totalRequirements === 0
    ) {
      return null;
    }
    return Math.round(
      (this.traceabilityMatrix.tracedRequirements /
        this.traceabilityMatrix.totalRequirements) *
        100,
    );
  }

  /** Verification coverage percentage (0-100) */
  get verificationCoverage(): number | null {
    return this.traceabilityMatrix?.verificationCoverage ?? null;
  }

  /** Number of untraced requirements */
  get untracedCount(): number {
    return this.traceabilityMatrix?.untracedRequirements ?? 0;
  }

  // ── Constructor / lifecycle ─────────────────────────────────────────────

  constructor(host: ReactiveControllerHost & HTMLElement) {
    this.host = host;
    host.addController(this);
  }

  hostConnected(): void {
    this._loadHistory();
  }

  hostDisconnected(): void {
    if (this._syncTimeout) {
      clearTimeout(this._syncTimeout);
      this._syncTimeout = null;
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Initialization
  // ══════════════════════════════════════════════════════════════════════════

  /** Set the API instance */
  initialize(api: SysmlApiAdapter): void {
    this.api = api;
  }

  /**
   * Initialize session - loads available models for the user to pick from.
   * Does NOT auto-restore or auto-create. User must explicitly select a model.
   */
  async initSession(): Promise<void> {
    sessionStorage.removeItem(MODEL_STORAGE_KEY);
    await this.loadModels();
  }

  /**
   * Initialize with a specific model ID (for linking to a document/workspace).
   */
  async initWithModel(id: string): Promise<void> {
    try {
      await this.loadModel(id);
    } catch {
      await this.initSession();
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Model CRUD
  // ══════════════════════════════════════════════════════════════════════════

  /** Load the list of available models */
  async loadModels(): Promise<void> {
    if (!this.api) return;
    this.isLoadingModels = true;
    this.error = null;
    try {
      this.models = await this.api.listModels();
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : 'Failed to load models';
      console.error('[ChatbotController] loadModels error:', err);
    } finally {
      this.isLoadingModels = false;
      this._update();
    }
  }

  /** Load a specific model by ID */
  async loadModel(id: string): Promise<void> {
    if (!this.api) return;
    this.isLoadingModels = true;
    this.error = null;
    try {
      const loadedModel = await this.api.getModel(id);
      this.modelId = loadedModel.id;
      this.model = loadedModel;
      this.modelState = loadedModel.state;
      this.messages = loadedModel.recentConversation ?? [];
      this.contextSummary = loadedModel.contextSummary ?? null;
      sessionStorage.setItem(MODEL_STORAGE_KEY, loadedModel.id);
      this._recalculateCompleteness();
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : 'Failed to load model';
      throw err;
    } finally {
      this.isLoadingModels = false;
      this._update();
    }
  }

  /** Create a new model */
  async createModel(
    name: string = 'New Model',
    intent?: string,
  ): Promise<void> {
    if (!this.api) return;
    this.isLoadingModels = true;
    this.error = null;
    try {
      const newModel = await this.api.createModel({ name, intent });
      this.modelId = newModel.id;
      this.model = newModel;
      this.modelState = newModel.state;
      this.messages = [];
      this.contextSummary = null;
      this.phase = 'exploring';
      this.completenessScore = 0;
      this.suggestions = [
        'A coffee machine',
        'A smart thermostat',
        'A drone system',
      ];
      this.suggestedSourceText = '';
      this.suggestedOutputs = [];
      this.qualityFeedback = null;
      this.traceabilityMatrix = null;
      sessionStorage.setItem(MODEL_STORAGE_KEY, newModel.id);
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : 'Failed to create model';
      throw err;
    } finally {
      this.isLoadingModels = false;
      this._update();
    }
  }

  /** Delete a model */
  async deleteModel(id: string): Promise<void> {
    if (!this.api) return;
    try {
      await this.api.deleteModel(id);
      this.models = this.models.filter((m) => m.id !== id);

      if (this.modelId === id) {
        this.modelId = null;
        this.model = null;
        this.modelState = null;
        this.messages = [];
        sessionStorage.removeItem(MODEL_STORAGE_KEY);
      }
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : 'Failed to delete model';
      throw err;
    } finally {
      this._update();
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Chat
  // ══════════════════════════════════════════════════════════════════════════

  /** Send a user message and get an AI response */
  async sendMessage(
    text: string,
    modelContext: ModelContext,
  ): Promise<ChatResponse | null> {
    if (!text.trim() || this.isLoading) return null;
    if (!this.api) return null;

    // Add user message immediately
    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      text: text.trim(),
      timestamp: new Date().toISOString(),
    };
    this.messages = [...this.messages, userMsg];
    this.isLoading = true;
    this.error = null;
    this._update();

    try {
      const response = await this.api.sendChatMessage(
        text,
        modelContext,
        this.modelId ?? undefined,
      );

      // Add assistant message
      if (response.message) {
        const assistantMsg: ChatMessage = {
          id: crypto.randomUUID(),
          role: 'assistant',
          text: response.message,
          timestamp: new Date().toISOString(),
          suggestedCode: response.suggestedCode,
        };
        this.messages = [...this.messages, assistantMsg];
      }

      // Update state from response
      this.phase = response.phase;

      if (response.modelState) {
        this.modelState = response.modelState;
      }
      if (typeof response.completenessScore === 'number') {
        this.completenessScore = response.completenessScore;
      } else {
        this._recalculateCompleteness();
      }
      if (response.suggestions?.length) {
        this.suggestions = response.suggestions;
      }
      if (response.validationIssues?.length) {
        this.validationIssues = response.validationIssues;
      }
      if (response.contextSummary) {
        this.contextSummary = response.contextSummary;
      }
      if (response.suggestedSourceText) {
        this.suggestedSourceText = response.suggestedSourceText;
      }
      if (response.suggestedCode) {
        this.suggestedSourceText = response.suggestedCode;
      }
      if (response.quality) {
        this.qualityFeedback = response.quality;
      }
      if (response.traceability) {
        this.traceabilityMatrix = response.traceability;
      }

      return response;
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Chat request failed';
      this.error = message;

      // Add error message
      const errorMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        text: 'Sorry, I encountered an error processing your request. Please try again.',
        timestamp: new Date().toISOString(),
      };
      this.messages = [...this.messages, errorMsg];

      // Remove the user message on error
      this.messages = this.messages.filter((m) => m.id !== userMsg.id);
      this.messages = [...this.messages, errorMsg];

      return null;
    } finally {
      this.isLoading = false;
      this._saveHistory();
      this._update();
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Generation
  // ══════════════════════════════════════════════════════════════════════════

  /** Generate SysML from the current model state */
  async generateSysML(): Promise<GenerateResponse | null> {
    if (!this.modelId || !this.api) return null;

    this.isGenerating = true;
    this.phase = 'generating';
    this.error = null;
    this._update();

    try {
      const response = await this.api.generateSysML(this.modelId);

      if (response.suggestedSourceText) {
        this.suggestedSourceText = response.suggestedSourceText;
      }
      if (response.suggestedOutputs?.length) {
        this.suggestedOutputs = response.suggestedOutputs;
      }

      return response;
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : 'Generation failed';
      return null;
    } finally {
      this.isGenerating = false;
      this.phase = 'ready';
      this._update();
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Suggestion management
  // ══════════════════════════════════════════════════════════════════════════

  /** Apply the current suggestion (caller inserts into editor) */
  applySuggestion(): string {
    const code = this.suggestedSourceText;
    this.suggestedSourceText = '';
    this._update();
    return code;
  }

  /** Dismiss the current suggestion without applying */
  dismissSuggestion(): void {
    this.suggestedSourceText = '';
    this._update();
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Quality scoring
  // ══════════════════════════════════════════════════════════════════════════

  /** Request a quality score update (legacy interface) */
  async refreshQualityScore(modelContext: ModelContext): Promise<void> {
    if (!this.api) return;
    try {
      this.qualityScore = await this.api.getQualityScore(modelContext);
      this.completenessScore = this.qualityScore.overall;
    } catch {
      // Non-fatal
    }
    this._update();
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Session management
  // ══════════════════════════════════════════════════════════════════════════

  /** Start a new session (clear current model and create a new one) */
  async startNewSession(): Promise<void> {
    this._resetState();
    this._clearHistory();
    this._update();
    await this.createModel('New Model');
  }

  /** Clear all messages and reset state */
  reset(): void {
    this._resetState();
    this._clearHistory();
    this._update();
  }

  /** Clear conversation history but keep the model */
  clearConversation(): void {
    this.messages = [];
    this.contextSummary = null;
    this.suggestions = [
      'A coffee machine',
      'A smart thermostat',
      'A drone system',
    ];
    this._clearHistory();
    this._update();
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Version history
  // ══════════════════════════════════════════════════════════════════════════

  /** Get version history for the current model */
  async getHistory(limit = 10): Promise<VersionHistoryEntry[]> {
    if (!this.modelId || !this.api) return [];
    try {
      return await this.api.getHistory(this.modelId, limit);
    } catch (err) {
      console.error('[ChatbotController] getHistory error:', err);
      return [];
    }
  }

  /** Revert to a previous version */
  async revertToVersion(version: number): Promise<void> {
    if (!this.modelId || !this.api) return;
    try {
      const reverted = await this.api.revertToVersion(
        this.modelId,
        version,
      );
      this.model = reverted;
      this.modelState = reverted.state;
      this._recalculateCompleteness();
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : 'Revert failed';
    }
    this._update();
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Direct State Updates — CRUD for model elements
  // ══════════════════════════════════════════════════════════════════════════

  // ── Parts ───────────────────────────────────────────────────────────────

  addPart(part: SysmlModelPart): void {
    if (!this.modelState) {
      this.modelState = { parts: [part] };
    } else if (!this.modelState.parts) {
      this.modelState.parts = [part];
    } else {
      this.modelState.parts = [...this.modelState.parts, part];
    }
    this._onModelStateChanged();
  }

  updatePart(name: string, updates: Partial<SysmlModelPart>): void {
    if (!this.modelState?.parts) return;
    const idx = this.modelState.parts.findIndex((p) => p.name === name);
    if (idx === -1) return;
    this.modelState.parts = this.modelState.parts.map((p, i) =>
      i === idx ? { ...p, ...updates } : p,
    );
    this._onModelStateChanged();
  }

  deletePart(name: string): void {
    if (!this.modelState?.parts) return;
    this.modelState.parts = this.modelState.parts.filter(
      (p) => p.name !== name,
    );
    if (this.modelState.parts.length === 0) this.modelState.parts = null;
    this._onModelStateChanged();
  }

  // ── Behaviors ───────────────────────────────────────────────────────────

  addBehavior(behavior: SysmlModelBehavior): void {
    if (!this.modelState) {
      this.modelState = { behaviors: [behavior] };
    } else if (!this.modelState.behaviors) {
      this.modelState.behaviors = [behavior];
    } else {
      this.modelState.behaviors = [...this.modelState.behaviors, behavior];
    }
    this._onModelStateChanged();
  }

  updateBehavior(
    name: string,
    updates: Partial<SysmlModelBehavior>,
  ): void {
    if (!this.modelState?.behaviors) return;
    const idx = this.modelState.behaviors.findIndex(
      (b) => b.name === name,
    );
    if (idx === -1) return;
    this.modelState.behaviors = this.modelState.behaviors.map((b, i) =>
      i === idx ? { ...b, ...updates } : b,
    );
    this._onModelStateChanged();
  }

  deleteBehavior(name: string): void {
    if (!this.modelState?.behaviors) return;
    this.modelState.behaviors = this.modelState.behaviors.filter(
      (b) => b.name !== name,
    );
    if (this.modelState.behaviors.length === 0)
      this.modelState.behaviors = null;
    this._onModelStateChanged();
  }

  // ── Requirements ────────────────────────────────────────────────────────

  addRequirement(req: SysmlModelRequirement): void {
    if (!this.modelState) {
      this.modelState = { requirements: [req] };
    } else if (!this.modelState.requirements) {
      this.modelState.requirements = [req];
    } else {
      this.modelState.requirements = [
        ...this.modelState.requirements,
        req,
      ];
    }
    this._onModelStateChanged();
  }

  updateRequirement(
    name: string,
    updates: Partial<SysmlModelRequirement>,
  ): void {
    if (!this.modelState?.requirements) return;
    const idx = this.modelState.requirements.findIndex(
      (r) => r.name === name,
    );
    if (idx === -1) return;
    this.modelState.requirements = this.modelState.requirements.map(
      (r, i) => (i === idx ? { ...r, ...updates } : r),
    );
    this._onModelStateChanged();
  }

  deleteRequirement(name: string): void {
    if (!this.modelState?.requirements) return;
    this.modelState.requirements = this.modelState.requirements.filter(
      (r) => r.name !== name,
    );
    if (this.modelState.requirements.length === 0)
      this.modelState.requirements = null;
    this._onModelStateChanged();
  }

  // ── Risks ───────────────────────────────────────────────────────────────

  addRisk(risk: SysmlModelRisk): void {
    if (!this.modelState) {
      this.modelState = { risks: [risk] };
    } else if (!this.modelState.risks) {
      this.modelState.risks = [risk];
    } else {
      this.modelState.risks = [...this.modelState.risks, risk];
    }
    this._onModelStateChanged();
  }

  updateRisk(name: string, updates: Partial<SysmlModelRisk>): void {
    if (!this.modelState?.risks) return;
    const idx = this.modelState.risks.findIndex((r) => r.name === name);
    if (idx === -1) return;
    this.modelState.risks = this.modelState.risks.map((r, i) =>
      i === idx ? { ...r, ...updates } : r,
    );
    this._onModelStateChanged();
  }

  deleteRisk(name: string): void {
    if (!this.modelState?.risks) return;
    this.modelState.risks = this.modelState.risks.filter(
      (r) => r.name !== name,
    );
    if (this.modelState.risks.length === 0) this.modelState.risks = null;
    this._onModelStateChanged();
  }

  // ── Verification Cases ──────────────────────────────────────────────────

  addVerificationCase(verification: SysmlModelVerificationCase): void {
    if (!this.modelState) {
      this.modelState = { verificationCases: [verification] };
    } else if (!this.modelState.verificationCases) {
      this.modelState.verificationCases = [verification];
    } else {
      this.modelState.verificationCases = [
        ...this.modelState.verificationCases,
        verification,
      ];
    }
    this._onModelStateChanged();
  }

  updateVerificationCase(
    name: string,
    updates: Partial<SysmlModelVerificationCase>,
  ): void {
    if (!this.modelState?.verificationCases) return;
    const idx = this.modelState.verificationCases.findIndex(
      (v) => v.name === name,
    );
    if (idx === -1) return;
    this.modelState.verificationCases =
      this.modelState.verificationCases.map((v, i) =>
        i === idx ? { ...v, ...updates } : v,
      );
    this._onModelStateChanged();
  }

  deleteVerificationCase(name: string): void {
    if (!this.modelState?.verificationCases) return;
    this.modelState.verificationCases =
      this.modelState.verificationCases.filter((v) => v.name !== name);
    if (this.modelState.verificationCases.length === 0)
      this.modelState.verificationCases = null;
    this._onModelStateChanged();
  }

  // ── Linking: verification <-> requirement ───────────────────────────────

  linkVerificationToRequirement(
    verificationName: string,
    requirementName: string,
  ): void {
    if (!this.modelState?.verificationCases) return;

    // Update verification case: add to verifies
    this.modelState.verificationCases =
      this.modelState.verificationCases.map((v) => {
        if (v.name !== verificationName) return v;
        const verifies = v.verifies ?? [];
        if (verifies.includes(requirementName)) return v;
        return { ...v, verifies: [...verifies, requirementName] };
      });

    // Update requirement: add to verifiedBy
    if (this.modelState.requirements) {
      this.modelState.requirements = this.modelState.requirements.map(
        (r) => {
          if (r.name !== requirementName) return r;
          const verifiedBy = r.verifiedBy ?? [];
          if (verifiedBy.includes(verificationName)) return r;
          return { ...r, verifiedBy: [...verifiedBy, verificationName] };
        },
      );
    }

    this._onModelStateChanged();
  }

  unlinkVerificationFromRequirement(
    verificationName: string,
    requirementName: string,
  ): void {
    if (!this.modelState?.verificationCases) return;

    // Update verification case: remove from verifies
    this.modelState.verificationCases =
      this.modelState.verificationCases.map((v) => {
        if (v.name !== verificationName) return v;
        return {
          ...v,
          verifies: (v.verifies ?? []).filter(
            (r) => r !== requirementName,
          ),
        };
      });

    // Update requirement: remove from verifiedBy
    if (this.modelState.requirements) {
      this.modelState.requirements = this.modelState.requirements.map(
        (r) => {
          if (r.name !== requirementName) return r;
          return {
            ...r,
            verifiedBy: (r.verifiedBy ?? []).filter(
              (v) => v !== verificationName,
            ),
          };
        },
      );
    }

    this._onModelStateChanged();
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Build model context from a modeler controller's state
  // ══════════════════════════════════════════════════════════════════════════

  buildModelContext(modeler: {
    partDefinitions: { length: number };
    requirements: { length: number };
    relationships: { length: number };
    verificationCases: { length: number };
    ports: { length: number };
    errorCount: number;
    warningCount: number;
    sourceText: string;
  }): ModelContext {
    return {
      partCount: modeler.partDefinitions.length,
      requirementCount: modeler.requirements.length,
      behaviorCount: this.modelState?.behaviors?.length ?? 0,
      relationshipCount: modeler.relationships.length,
      verificationCount: modeler.verificationCases.length,
      portCount: modeler.ports.length,
      riskCount: this.modelState?.risks?.length ?? 0,
      errorCount: modeler.errorCount,
      warningCount: modeler.warningCount,
      sourceText: modeler.sourceText,
    };
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Internal: persistence
  // ══════════════════════════════════════════════════════════════════════════

  private _loadHistory(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.CHAT_HISTORY);
      if (raw) {
        this.messages = JSON.parse(raw);
        if (this.messages.length > 0) this.phase = 'ready';
      }
    } catch {
      this.messages = [];
    }
  }

  private _saveHistory(): void {
    try {
      // Keep last 100 messages
      const trimmed = this.messages.slice(-100);
      localStorage.setItem(
        STORAGE_KEYS.CHAT_HISTORY,
        JSON.stringify(trimmed),
      );
    } catch {
      // Storage full -- ignore
    }
  }

  private _clearHistory(): void {
    try {
      localStorage.removeItem(STORAGE_KEYS.CHAT_HISTORY);
    } catch {
      // Ignore
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Internal: state management
  // ══════════════════════════════════════════════════════════════════════════

  /**
   * Calculate completeness score from model state.
   * Formula: parts*10 + behaviors*20 + requirements*20 + risks*10 (max 100)
   */
  private _recalculateCompleteness(): void {
    const parts = this.modelState?.parts?.length ?? 0;
    const behaviors = this.modelState?.behaviors?.length ?? 0;
    const requirements = this.modelState?.requirements?.length ?? 0;
    const risks = this.modelState?.risks?.length ?? 0;
    this.completenessScore = Math.min(
      100,
      parts * 10 + behaviors * 20 + requirements * 20 + risks * 10,
    );
  }

  /**
   * Called after any direct state mutation.
   * Recalculates completeness and syncs to backend (debounced).
   */
  private _onModelStateChanged(): void {
    this._recalculateCompleteness();
    this._syncModelStateToBackend();
    this._update();
  }

  /**
   * Sync model state to backend (debounced 500ms)
   */
  private _syncModelStateToBackend(): void {
    if (!this.modelId || !this.modelState || !this.api) return;

    if (this._syncTimeout) {
      clearTimeout(this._syncTimeout);
    }

    this._syncTimeout = setTimeout(async () => {
      try {
        const result: UpdateModelStateResult =
          await this.api.updateModelState(this.modelId!, this.modelState!);
        if (result.quality) {
          this.qualityFeedback = result.quality;
          this._update();
        }
      } catch (err) {
        console.error(
          '[ChatbotController] Failed to sync model state:',
          err,
        );
      }
    }, 500);
  }

  /** Reset all state to defaults */
  private _resetState(): void {
    this.modelId = null;
    this.model = null;
    this.modelState = null;
    this.messages = [];
    this.phase = 'exploring';
    this.completenessScore = null;
    this.suggestions = [
      'A coffee machine',
      'A smart thermostat',
      'A drone system',
    ];
    this.validationIssues = [];
    this.suggestedSourceText = '';
    this.suggestedOutputs = [];
    this.contextSummary = null;
    this.qualityFeedback = null;
    this.traceabilityMatrix = null;
    this.qualityScore = null;
    this.error = null;
    sessionStorage.removeItem(MODEL_STORAGE_KEY);
  }

  private _update(): void {
    this.generation++;
    this.host.requestUpdate();
  }
}
